import { AllHTMLAttributes, forwardRef, useRef, useState } from 'react';
import { Close } from '@mui/icons-material';
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import clsx from 'clsx';
import lodash from 'lodash';
import { Control, Controller, ControllerProps } from 'react-hook-form';
import { ErrorFormField } from '../types';

type CustomSelectProps = AllHTMLAttributes<HTMLDivElement> &
  ErrorFormField & {
    options: any[];
    placeholder?: string;
    defaultValue?: string;
    [key: string]: any;
    hideClose?: boolean;
  };

const CustomSelect = forwardRef((props: CustomSelectProps, ref) => {
  const {
    className,
    options = [],
    placeholder = 'Required field',
    label = '',
    defaultValue = '',
    onChange,
    error,
    errMessage,
    hideClose,
    ...extraProps
  } = props as any;
  const [selectValue, setSelectValue] = useState(defaultValue);
  const uid = useRef(lodash.uniqueId());

  const selectChangeAction = (e) => {
    let value = e.target.value;
    setSelectValue(value);
    if (onChange) onChange(value);
  };

  const cleanAction = () => {
    setSelectValue('');
    if (onChange) onChange('');
  };

  return (
    <FormControl className={clsx('relative', className)}>
      <InputLabel id={uid.current + '-label'}>{label}</InputLabel>
      {selectValue && !hideClose && (
        <Box className="bg-red pointer-events-none absolute z-10 flex h-full w-full flex-row items-center justify-end px-6">
          <IconButton className="pointer-events-auto" onClick={cleanAction}>
            <Close className="h-5 w-5" />
          </IconButton>
        </Box>
      )}
      <Select
        ref={ref}
        id={uid.current}
        labelId={uid.current + '-label'}
        value={selectValue}
        label={label}
        onChange={selectChangeAction}
        error={error}
        {...extraProps}
      >
        {options.map((item) => {
          return (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          );
        })}
      </Select>

      {error && (
        <FormHelperText className="mt-1" error={error}>
          {errMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
});

export const SelectNoneValue = 'select-none';

type ControlledProps = Omit<ControllerProps, 'control' | 'render'> &
  CustomSelectProps & {
    control: Control<any>;
  };

export const SelectControlled = forwardRef(
  ({ control, name, ...props }: ControlledProps, ref) => {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <CustomSelect
              ref={ref}
              {...props}
              defaultValue={value}
              onChange={onChange}
              error={!!error}
              errMessage={error?.message}
            />
          );
        }}
      />
    );
  }
);

export default CustomSelect;
